// ===================================================================
// KAIXO - PlanStatusBanner
// ===================================================================
// Banner global que avisa al usuario de:
//   · Plan expira pronto (≤7 días)        → fondo ámbar
//   · Plan ya expiró (volvió a FREE)      → fondo rojo
//   · Pago fallido pendiente              → fondo rojo
//
// Se renderiza en App.js encima de todo (z-index alto pero por debajo
// de modales bloqueantes). Aparece solo si hay algo que avisar — si no,
// no estorba.
//
// Cumplimiento Apple/Google: que el usuario sepa con tiempo cuándo va
// a renovar y cómo cancelar; banners visibles + acción a 1 tap.
// ===================================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import usePlan from '../../hooks/usePlan';
import PlansModal from '../Plans/PlansModal';
import MyBillingModal from '../MyBilling/MyBillingModal';
import './PlanStatusBanner.css';

const I18N = {
    español: {
        expiringIn: (n) => `Tu plan expira en ${n} ${n === 1 ? 'día' : 'días'}`,
        expiringDesc: 'Renueva ahora para no perder tus funciones premium.',
        expired: 'Tu plan ha expirado',
        expiredDesc: 'Has vuelto al plan FREE. Puedes reactivar en cualquier momento.',
        paymentFailed: 'Hubo un problema con tu último pago',
        paymentFailedDesc: 'Tu plan se quedará suspendido. Revisa tu método de pago.',
        manage: 'Gestionar',
        details: 'Ver detalles',
        dismiss: '✕'
    },
    inglés: {
        expiringIn: (n) => `Your plan expires in ${n} ${n === 1 ? 'day' : 'days'}`,
        expiringDesc: 'Renew now to keep your premium features.',
        expired: 'Your plan has expired',
        expiredDesc: 'You are back on the FREE plan. Reactivate any time.',
        paymentFailed: 'There was a problem with your last payment',
        paymentFailedDesc: 'Your plan will be suspended. Update your payment method.',
        manage: 'Manage',
        details: 'See details',
        dismiss: '✕'
    },
    francés: {
        expiringIn: (n) => `Ton plan expire dans ${n} ${n === 1 ? 'jour' : 'jours'}`,
        expiringDesc: 'Renouvelle maintenant pour garder tes fonctions premium.',
        expired: 'Ton plan a expiré',
        expiredDesc: 'Tu es repassé au plan FREE. Réactive quand tu veux.',
        paymentFailed: 'Problème avec ton dernier paiement',
        paymentFailedDesc: 'Ton plan sera suspendu. Vérifie ta méthode de paiement.',
        manage: 'Gérer',
        details: 'Voir détails',
        dismiss: '✕'
    },
    alemán: {
        expiringIn: (n) => `Dein Tarif läuft in ${n} ${n === 1 ? 'Tag' : 'Tagen'} ab`,
        expiringDesc: 'Verlängere jetzt, um Premium-Funktionen zu behalten.',
        expired: 'Dein Tarif ist abgelaufen',
        expiredDesc: 'Du bist wieder im FREE-Tarif. Jederzeit reaktivierbar.',
        paymentFailed: 'Problem mit deiner letzten Zahlung',
        paymentFailedDesc: 'Dein Tarif wird ausgesetzt. Aktualisiere die Zahlungsmethode.',
        manage: 'Verwalten',
        details: 'Details ansehen',
        dismiss: '✕'
    },
    portugués: {
        expiringIn: (n) => `O teu plano expira em ${n} ${n === 1 ? 'dia' : 'dias'}`,
        expiringDesc: 'Renova agora para manter as funções premium.',
        expired: 'O teu plano expirou',
        expiredDesc: 'Voltaste ao plano FREE. Reativa quando quiseres.',
        paymentFailed: 'Problema com o último pagamento',
        paymentFailedDesc: 'O teu plano vai ser suspenso. Verifica o método de pagamento.',
        manage: 'Gerir',
        details: 'Ver detalhes',
        dismiss: '✕'
    },
    italiano: {
        expiringIn: (n) => `Il tuo piano scade tra ${n} ${n === 1 ? 'giorno' : 'giorni'}`,
        expiringDesc: 'Rinnova ora per mantenere le funzioni premium.',
        expired: 'Il tuo piano è scaduto',
        expiredDesc: 'Sei tornato al piano FREE. Riattivalo quando vuoi.',
        paymentFailed: 'Problema con il tuo ultimo pagamento',
        paymentFailedDesc: 'Il piano sarà sospeso. Aggiorna il metodo di pagamento.',
        manage: 'Gestisci',
        details: 'Vedi dettagli',
        dismiss: '✕'
    },
    euskera: {
        expiringIn: (n) => `Zure plana ${n} ${n === 1 ? 'egun' : 'egunetan'} iraungiko da`,
        expiringDesc: 'Berritu orain premium funtzioak ez galtzeko.',
        expired: 'Zure plana iraungi da',
        expiredDesc: 'FREE planera itzuli zara. Berriz aktibatu nahi duzunean.',
        paymentFailed: 'Arazo bat egon da azken ordainketarekin',
        paymentFailedDesc: 'Zure plana etenda geratuko da. Egiaztatu ordainketa modua.',
        manage: 'Kudeatu',
        details: 'Xehetasunak ikusi',
        dismiss: '✕'
    }
};

function pickL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return I18N[u?.language] || I18N.inglés;
    } catch { return I18N.inglés; }
}

const DISMISS_KEY = 'kaixo_plan_banner_dismissed_until';

function isDismissedRecently() {
    try {
        const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        return until && Date.now() < until;
    } catch { return false; }
}
function dismissForHours(hours) {
    try {
        localStorage.setItem(DISMISS_KEY, String(Date.now() + hours * 3600 * 1000));
    } catch {}
}

export default function PlanStatusBanner() {
    const { plan, daysLeft, isExpired, tier } = usePlan();
    const [showPlans, setShowPlans] = useState(false);
    const [showBilling, setShowBilling] = useState(false);
    const [dismissed, setDismissed] = useState(isDismissedRecently());
    const L = pickL();

    // Re-evaluar dismissed cuando el plan cambia (volvieron a renovar -> mostrar el siguiente aviso)
    useEffect(() => { setDismissed(isDismissedRecently()); }, [plan?.tier, plan?.expiresAt]);

    if (!plan || dismissed) return null;

    // Detectar pago fallido — el backend puede marcar `lastPaymentFailed`
    // o el subscription.status === 'past_due'. Lo leemos de plan.* si existe.
    const paymentFailed = plan?.lastPaymentFailed === true || plan?.subscriptionStatus === 'past_due';

    let kind = null; // 'expiring' | 'expired' | 'failed'
    if (paymentFailed) kind = 'failed';
    else if (isExpired && tier === 'FREE') kind = 'expired';
    else if (typeof daysLeft === 'number' && daysLeft >= 0 && daysLeft <= 7 && tier !== 'FREE') kind = 'expiring';

    if (!kind) return null;

    const handleAction = () => {
        if (kind === 'failed') setShowBilling(true);
        else setShowPlans(true);
    };

    const handleDismiss = () => {
        // Dismissed por 24h — al día siguiente vuelve a aparecer
        dismissForHours(24);
        setDismissed(true);
    };

    let title, desc, level;
    if (kind === 'failed') { title = L.paymentFailed; desc = L.paymentFailedDesc; level = 'danger'; }
    else if (kind === 'expired') { title = L.expired; desc = L.expiredDesc; level = 'danger'; }
    else { title = L.expiringIn(daysLeft); desc = L.expiringDesc; level = 'warning'; }

    return ReactDOM.createPortal(
        <div className={`psb-banner psb-${level}`} role="alert">
            <div className="psb-content">
                <strong className="psb-title">{title}</strong>
                <span className="psb-desc">{desc}</span>
            </div>
            <button className="psb-action" onClick={handleAction}>
                {kind === 'failed' ? L.details : L.manage}
            </button>
            <button className="psb-dismiss" onClick={handleDismiss} aria-label={L.dismiss}>
                {L.dismiss}
            </button>
            {showPlans && <PlansModal onClose={() => setShowPlans(false)} />}
            {showBilling && <MyBillingModal onClose={() => setShowBilling(false)} />}
        </div>,
        document.body
    );
}
